<cs-header>
  <cs-header-title>{{ LL.recipeScanner.title() }}</cs-header-title>
</cs-header>

<cs-content>
  <!-- Camera Permission Denied -->
  @if (cameraPermissionDismissed) {
    <cs-scanner-permission-denied [message]="LL.recipeScanner.cameraPermissionDenied()" [scanner]="ScannerName.Recipe"></cs-scanner-permission-denied>
  }

  <!-- Dev-only MOCK button (gated on settings.testMode) — shown regardless of camera permission
       so testers can exercise the results-screen flow in a browser without camera access. -->
  @if (showMockButton && phase === 'camera') {
  <div class="dev-mock-bar">
    <cs-button secondary small [label]="mockButtonLabel" (click)="mockAnalyze($event)"></cs-button>
  </div>
  }

  <!-- Phase 1: Camera (stays mounted through analysis so the collage shows) -->
  @if (!cameraPermissionDismissed && phase === 'camera') {
    <cs-scanner-camera-stage #stage
      [photos]="capturedPhotos"
      [analyzing]="analyzing"
      [progress]="analyzeProgress"
      [statusText]="LL.recipeScanner.analyzing()"
      [retryMessage]="retryMessage"
      [suppressAnalyzingOverlay]="true"
      (cameraReady)="onCameraStateChange()"
      (cameraError)="onCameraError($event)"
      (permissionChange)="onCameraStateChange()">
    </cs-scanner-camera-stage>

    @if (!analyzing) {
      <cs-scanner-photo-reel #reel
        [photos]="capturedPhotos"
        [maxSlots]="MAX_RECIPE_SCAN_IMAGES"
        [canAdd]="!stage.cameraView?.isActive"
        (remove)="removePhotoAt($event)"
        (inspect)="inspectPhotoAt($event)"
        (add)="addPhoto()">
      </cs-scanner-photo-reel>

      <cs-scanner-hints [hints]="photoHints"></cs-scanner-hints>

      <!-- Shutter bar: upload (+ model picker) left, shutter center, Analyze right -->
      <cs-scanner-shutter-bar
        [shutterHidden]="!stage.cameraView?.isActive"
        [shutterDisabled]="allPhotosTaken"
        [nativeShutterSound]="stage.cameraView?.isNative"
        (snap)="snapPhoto()">
        <div left class="left-buttons">
          <cs-button icon="add" secondary (click)="openFilePicker()" [disabled]="allPhotosTaken"></cs-button>
          @if (modelSelection.scanMode === 'any') {
            <cs-scanner-model-picker
              [modelOptions]="modelSelection.modelOptions"
              [selectedModel]="modelSelection.selectedModel"
              (modelSelected)="onModelSelected($event)">
            </cs-scanner-model-picker>
          }
        </div>
        <div right>
          @if (hasPhotos && !allPhotosTaken) {
            <cs-button primary small [label]="LL.recipeScanner.analyzeButton()" (click)="analyzeRecipe()"></cs-button>
          }
        </div>
      </cs-scanner-shutter-bar>
    } @else {
      <div class="scanner-bottom-bar">
        <cs-scanner-analyzing-overlay
          [statusText]="LL.recipeScanner.analyzing()"
          [progress]="analyzeProgress"
          [retryMessage]="retryMessage">
        </cs-scanner-analyzing-overlay>
      </div>
    }
  }

  <!-- Phase 2: Results -->
  @if (phase === 'results') {
    <cs-card-list>
      <!-- Recipe details card: editable name, servings, and a read-only preview of the
           extracted instructions (fully editable later on the Create Recipe page). Sits
           as the first card so it lines up with the ingredient cards below. -->
      <cs-card class="recipe-details-card">
        <cs-card-content>
          <div class="recipe-details-fields">
            <cs-form-field [label]="LL.recipeScanner.nameLabel()">
              <cs-input [(value)]="recipeName" [placeholder]="LL.recipeScanner.namePlaceholder()"></cs-input>
            </cs-form-field>
            @if (servings) {
              <cs-note small>{{ servingsLabel }}</cs-note>
            }
            @if (scannedInstructions) {
              <cs-form-field [label]="LL.recipeScanner.instructionsLabel()">
                <cs-textarea [(value)]="scannedInstructions" [rows]="5" autoadjustheight></cs-textarea>
              </cs-form-field>
            }
          </div>
        </cs-card-content>
      </cs-card>
      <!-- AI-chosen recipe image (the cooked dish), only when one was identified. The
           checkbox by the title lets the user accept or skip using it as the recipe image. -->
      @if (recipeImageDataUrl) {
        <cs-card class="recipe-image-card">
          <cs-card-title>
            <div class="recipe-image-title">
              <cs-checkbox [(checked)]="useRecipeImage"></cs-checkbox>
              <span>{{ LL.recipeScanner.imageLabel() }}</span>
            </div>
          </cs-card-title>
          <cs-card-content>
            <img class="recipe-image-preview" [src]="recipeImageDataUrl" [class.dimmed]="!useRecipeImage" alt="" />
          </cs-card-content>
        </cs-card>
      }
      @for (item of resultItems; track item.rowId) {
        <cs-food-card
          [food]="item.foodHeader"
          [nutrients]="item.nutrients"
          [description]="item.subtitle"
          [showMenuButton]="true"
          [menuIcon]="item.matched ? 'refresh' : 'search'"
          [menuLabel]="item.matched ? LL.recipeScanner.changeButton() : LL.recipeScanner.searchButton()"
          (menuClick)="item.matched ? replaceFood(item) : searchFood(item)"
          [checkable]="true"
          [checked]="item.checked"
          (checkedChange)="onItemToggled(item, $event)"
          (click)="onItemClick(item)">
        </cs-food-card>
      }
    </cs-card-list>
  }
</cs-content>

<!-- Analyze + Retake buttons, once all photos are captured, stacked vertically -->
@if (phase === 'camera' && allPhotosTaken && !analyzing) {
  <cs-page-action-buttons class="vertical">
    <cs-button primary [label]="LL.recipeScanner.analyzeButton()" (click)="analyzeRecipe()"></cs-button>
    <cs-button secondary [label]="LL.recipeScanner.retakeButton()" (click)="retakeAllPhotos()"></cs-button>
  </cs-page-action-buttons>
}

<!-- Use Recipe + Retake buttons, stacked vertically -->
@if (phase === 'results') {
  <cs-page-action-buttons class="vertical">
    <cs-button primary
      [label]="LL.recipeScanner.useRecipeButton({ count: checkedCount })"
      [disabled]="checkedCount === 0"
      (click)="useRecipe()">
    </cs-button>
    <cs-button secondary [label]="LL.recipeScanner.retakeButton()" (click)="retakeFromResults()"></cs-button>
  </cs-page-action-buttons>
}
